import { useMemo } from 'react';
import { useFilters } from '@/context/FilterContext';
import { useMensalidades } from '@/context/MensalidadesContext';
import { computeCanonicalIndicators } from '@/lib/canonicalIndicators';
import type { CanonicalIndicators } from '@/types/Aggregates';
import type { Period } from '@/types/Filters';
import type { Transaction } from '@/types/Transaction';

/** Converte Period em range concreto, ou null se 'all'. */
export function periodToRange(period: Period, now: Date = new Date()): { from: Date; to: Date } | null {
  switch (period.preset) {
    case 'all':
      return null;
    case 'custom':
      return period.customRange ?? null;
    case 'current-month':
      return {
        from: new Date(now.getFullYear(), now.getMonth(), 1),
        to: new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999),
      };
    case 'quarter': {
      const q = Math.floor(now.getMonth() / 3);
      return {
        from: new Date(now.getFullYear(), q * 3, 1),
        to: new Date(now.getFullYear(), q * 3 + 3, 0, 23, 59, 59, 999),
      };
    }
    case 'calendar-year':
      return {
        from: new Date(now.getFullYear(), 0, 1),
        to: new Date(now.getFullYear(), 11, 31, 23, 59, 59, 999),
      };
  }
}

/** Aplica filtros (period + suppliers + categories) sobre transactions. */
export function applyFilters(
  transactions: Transaction[],
  filters: {
    period: Period;
    suppliers: string[];
    categories: string[];
  },
  now: Date = new Date(),
): Transaction[] {
  let result = transactions;
  const range = periodToRange(filters.period, now);
  if (range) {
    result = result.filter((t) => t.date >= range.from && t.date <= range.to);
  }
  if (filters.suppliers.length > 0) {
    const set = new Set(filters.suppliers);
    result = result.filter((t) => set.has(t.supplier));
  }
  if (filters.categories.length > 0) {
    const set = new Set(filters.categories);
    result = result.filter((t) => set.has(t.category));
  }
  return result;
}

export interface FilteredTransactionsResult {
  /** Transacoes apos aplicar filtros. */
  filtered: Transaction[];
  /** filtered.length. */
  count: number;
  /** Total antes de filtros (= transactions.length do hook base). */
  totalCount: number;
  /** True se algum filtro estiver activo (i.e. filtered != transactions). */
  isFiltered: boolean;
  /** Indicadores recalculados sobre o subset filtrado. */
  indicators: CanonicalIndicators | null;
}

/**
 * Hook composto que aplica filtros sobre transactions e re-calcula
 * indicadores. Memo-friendly — recalcula apenas quando transactions ou
 * filters mudam.
 */
export function useFilteredTransactions(): FilteredTransactionsResult {
  const { state } = useMensalidades();
  const { filters } = useFilters();

  return useMemo(() => {
    const transactions = state.transactions;
    const totalCount = transactions.length;
    const filtered = applyFilters(transactions, filters);
    const count = filtered.length;
    const isFiltered = count !== totalCount;
    const indicators =
      state.expected != null ? computeCanonicalIndicators(filtered, state.expected) : null;
    return { filtered, count, totalCount, isFiltered, indicators };
  }, [state.transactions, state.expected, filters]);
}
